
import BaseAlert from '@/components/base/BaseAlert.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseExpansion from '@/components/base/BaseExpansion.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseSection from '@/components/base/BaseSection.vue';
import BaseSpinner from '@/components/base/BaseSpinner.vue';
import BaseSvg from '@/components/base/BaseSvg.vue';
import BookingBasket from '@/components/booking/basket/BookingBasket.vue';
import BookingHeader from '@/components/bookingflow/BookingHeader.vue';
import useBookingNavigation from '@/composables/useBookingNavigation';
import useCalculatePrice from '@/composables/useCalculatePrice';
import usePayment from '@/composables/usePayment';
import { cardTypes } from '@/config/cardTypes';
import { getQueryParamValue } from '@/helpers/urlHelper';
import { PayResult } from '@/models/www/PayResult';
import { useBookingSummaryStore } from '@/store/basket';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { maska } from 'maska';
import useRuntimeConfig from '@/composables/useRuntimeConfig';
import { computed, defineComponent, nextTick, onMounted, reactive, ref } from 'vue';
import useBooking from '@/composables/useBooking';
import { useEventBus } from '@vueuse/core';
import { DialogMessage, DialogType, dialogKey } from '@/models/symbols/Dialog';
import { useI18n } from "vue-i18n";

export default defineComponent({
	directives: { maska },
	components: {
		BookingHeader,
		BaseSection,
		BaseSvg,
		BaseInput,
		BaseButton,
		BookingBasket,
		BaseExpansion,
		BaseAlert,
		BaseSpinner
	},
	setup() {
		const { getRuntimeConfig } = useRuntimeConfig();
		const { cancelActiveBooking } = useBooking();
		const bus = useEventBus<DialogMessage>(dialogKey);
		const { t } = useI18n();

		getRuntimeConfig().then((x) => {
			const devMode = x.apiBaseUrl.indexOf('https://api.islesofscilly-travel.co.uk') === -1;

			//need to append opayo/sagepay script
			//to be able to communiocate with their api
			if (!document.querySelector('#sagepay')) {
				let recaptchaScript = document.createElement('script');
				recaptchaScript.id = 'sagepay';
				recaptchaScript.setAttribute(
					'src',
					devMode ? 'https://sandbox.opayo.eu.elavon.com/api/v1/js/sagepay.js' : 'https://live.opayo.eu.elavon.com/api/v1/js/sagepay.js'
				);
				document.body.appendChild(recaptchaScript);
			}
		});

		const bookingSummaryStore = useBookingSummaryStore();
		const { prepareForPayment, startPayment, validateCard } = usePayment();
		const { setSummary } = useCalculatePrice();

		const showBasket = ref<boolean>(false);
		const merchantSessionKey = ref<string>();
		const loading = ref<boolean>(false);
		const payResult = ref<PayResult>();
		const errors = ref<string[]>([]);
		const initializing = ref(false);

		//3DSecureForm to submit to go to payment
		const threeDSecureForm = ref();

		const state = reactive({
			name: '',
			cardNumber: '',
			expiryDate: '',
			cvc: ''
		});

		const rules = {
			name: { required },
			cardNumber: { required },
			expiryDate: { required },
			cvc: { required }
		};

		const v$ = useVuelidate(rules, state);

		const { prevStep, currentStepIsValid, loadingNextStep } = useBookingNavigation();

		const toggleBasket = () => {
			showBasket.value = !showBasket.value;
		};

		const bookingSummary = computed(() => {
			return bookingSummaryStore.getBookingSummary;
		});

		const init = async () => {
			const result = await prepareForPayment();

			if (result) {
				merchantSessionKey.value = result.merchantKey;
				setSummary(result.booking);
			}
		};

		const initiatePayment = async () => {
			try {
				(window as any)
					.sagepayOwnForm({
						merchantSessionKey: merchantSessionKey.value
					})
					.tokeniseCardDetails({
						cardDetails: {
							cardholderName: state.name,
							cardNumber: state.cardNumber.replace(/\s/g, ''),
							expiryDate: state.expiryDate.replace('/', ''),
							securityCode: state.cvc
						},
						onTokenised: async (result: any) => {
							if (result.success) {
								startPayment({
									...result,
									browserLanguage: navigator.language,
									javaEnabled: navigator.javaEnabled,
									colorDepth: screen.colorDepth,
									screenHeight: screen.height,
									screenWidth: screen.width,
									timeZone: new Date().getTimezoneOffset(),
									merchantKey: merchantSessionKey.value
								})
									.then((res) => {
										if (res) {
											payResult.value = res;

											nextTick(() => {
												threeDSecureForm.value.submit();
											});
										}
									})
									.catch(async (err) => {
										await init();

										errors.value = [];
										errors.value.push(err.message);
									})
									.finally(() => (loading.value = false));
							} else {
								await init();

								errors.value = [];
								for (const err in result.errors) {
									errors.value.push((result.errors[err] as any).message);
								}

								loading.value = false;
							}
						}
					});
			} catch (err: any) {
				await init();

				errors.value = [];
				errors.value.push(err.message);
				loading.value = false;
			}
		};
		
		const forward = async () => {
			v$.value.$touch();
			const isValid = await v$.value.$validate();
			if (!isValid) {
				const firstError = (document as any).querySelector('.validation-container');
				if (firstError) {
					firstError.scrollIntoView({ block: 'center', behavior: 'smooth' });
				}
				return;
			}

			loading.value = true;
			validateCard(state.cardNumber.replace(/\s/g, '')).then((valid) => {
				initiatePayment();
			})
			.catch ((err: any) => {
					errors.value = [];
					errors.value.push(err.message);
			})
			.finally(() => {
					loading.value = false;
			});
			
		};

		onMounted(async () => {
			initializing.value = true;
			await init();
			const err = getQueryParamValue('error');
			if (err) {
				errors.value.push(err);
			}
			const status = getQueryParamValue('transaction_status');
			if (status) {
				switch (status) {
					case '2001':
						errors.value.push('Transaction was not processed and no money has been withdrawn from your account');
						break;
					default:
						break;
				}
			}
			initializing.value = false;
		});

		const cancelAccommodationBooking = () => {
			bus.emit({
				title: t("messages.cancel.title"),
				message: t("messages.cancel.body"),
				yes: t("button.yes"),
				no: t("button.no"),
				callback: () => {
					loading.value = true;

					getRuntimeConfig().then((x) =>
						cancelActiveBooking(
							`${x.accommodationUrl}`
						)
					);
				},
				type: DialogType.Warning,
			} as DialogMessage);
		};

		return {
			bookingSummary,
			toggleBasket,
			showBasket,
			prevStep,
			currentStepIsValid,
			forward,
			loadingNextStep,
			v$,
			cardTypes,
			loading,
			payResult,
			threeDSecureForm,
			errors,
			initializing,
			cancelAccommodationBooking
		};
	}
});
